import React, { useState, useCallback } from 'react';
import { Home } from 'lucide-react';
import sendPaintQualityEmail from './paintQualityEmailService';
import { validateFormCompletion, markFormCompleted, lookupVesselBySerial, isVesselScrapped, getScrappedFormMessage } from './formTraceabilityService';
import SerialValidationModal from './SerialValidationModal';
import PVSPageLayout from './PVSPageLayout';
import FormSuccessModal from './FormSuccessModal';

const getPreviousWorkingDay = () => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  
  let daysToSubtract = 1;
  if (dayOfWeek === 1) {
    daysToSubtract = 3;
  } else if (dayOfWeek === 0) {
    daysToSubtract = 2;
  }
  
  const previousDay = new Date(today);
  previousDay.setDate(today.getDate() - daysToSubtract);
  return previousDay.toISOString().split('T')[0];
};

const PaintQualityInspectionForm = ({ onBack }) => {
  const savedPrimerBatch = localStorage.getItem('pvs_primerBatchNumber') || '';
  const savedTopcoatBatch = localStorage.getItem('pvs_topcoatBatchNumber') || '';

  const [formData, setFormData] = useState({
    vesselSerial: '',
    capacityLitres: '',
    customerName: '',
    datePainted: getPreviousWorkingDay(),
    primerPaintUsed: 'HSP ZP LT Penguard',
    topcoatPaintUsed: '12B21 Jotun Hardtop Flexi',
    primerBatchNumber: savedPrimerBatch,
    topcoatBatchNumber: savedTopcoatBatch,
    inspectionDate: new Date().toISOString().split('T')[0],
    inspectorName: 'Adrian Bates',
    primerApplicationMethod: 'Airless',
    primerThinnerUsed: 'Jotun Thinner No 17',
    topcoatApplicationMethod: 'Airless',
    topcoatThinnerUsed: 'Jotun Thinner No 10',
    primerOvenBakeCycleUsed: 'no',
    primerOvenTemperature: '',
    primerOvenDuration: '',
    topcoatOvenBakeCycleUsed: 'no',
    topcoatOvenTemperature: '',
    topcoatOvenDuration: '',
    requiredDFTMin: '200',
    requiredDFTMax: '400',
    dftLow: '',
    dftHigh: '',
    dftAverage: '',
    coverageUniform: 'no',
    runsOrSags: 'no',
    pinholes: 'no',
    orangePeel: 'no',
    drySpray: 'no',
    undersideFullyCovered: 'no',
    dataPlatesFreeOfOverspray: 'no',
    finalStatus: '',
    releaseNotes: '',
    additionalComments: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [serialModalOpen, setSerialModalOpen] = useState(false);
  const [serialModalMessage, setSerialModalMessage] = useState('');
  const [serialLookupState, setSerialLookupState] = useState({ loading: false, notFound: false, lastLookup: '' });
  const [missingVesselFields, setMissingVesselFields] = useState({});
  const MISSING_PLACEHOLDER = 'Unable to obtain — please enter';
  const closeSerialModal = useCallback(() => setSerialModalOpen(false), []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (name === 'vesselSerial' && serialLookupState.lastLookup) {
      setSerialLookupState({ loading: false, notFound: false, lastLookup: '' });
      setMissingVesselFields({});
    }
  };

  const handleSerialLookup = useCallback(async () => {
    const serial = String(formData.vesselSerial || '').trim();
    if (!serial || serial === serialLookupState.lastLookup) return;

    setSerialLookupState({ loading: true, notFound: false, lastLookup: serial });
    try {
      const result = await lookupVesselBySerial(serial);
      if (!result.found) {
        setSerialLookupState({ loading: false, notFound: true, lastLookup: serial });
        setMissingVesselFields({});
        return;
      }

      const vessel = result.vessel || {};
      const missing = {};
      const hasOwner = !!vessel.owner;
      const hasVesselSize = vessel.vesselSize !== null && vessel.vesselSize !== undefined && vessel.vesselSize !== '';

      if (!hasOwner) missing.customerName = true;
      if (!hasVesselSize) missing.capacityLitres = true;

      setFormData(prev => {
        const next = { ...prev };
        if (!prev.customerName && hasOwner) {
          next.customerName = vessel.owner;
        }
        if ((prev.capacityLitres === '' || prev.capacityLitres === null || prev.capacityLitres === undefined)
            && hasVesselSize) {
          next.capacityLitres = String(vessel.vesselSize);
        }
        return next;
      });
      setMissingVesselFields(missing);
      setSerialLookupState({ loading: false, notFound: false, scrapped: isVesselScrapped(vessel), lastLookup: serial });
    } catch (error) {
      console.warn('Vessel lookup failed:', error);
      setSerialLookupState({ loading: false, notFound: false, lastLookup: serial });
    }
  }, [formData.vesselSerial, serialLookupState.lastLookup]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const mandatoryFields = {
      vesselSerial: 'Vessel Serial',
      customerName: 'Customer Name',
      inspectorName: 'Inspector Name',
      inspectionDate: 'Inspection Date',
      finalStatus: 'Final Status'
    };
    
    const missingFields = [];
    for (const [field, label] of Object.entries(mandatoryFields)) {
      if (!formData[field] || formData[field].trim() === '') {
        missingFields.push(label);
      }
    }
    
    if (missingFields.length > 0) {
      setSubmitMessage(`Please fill in the following mandatory fields: ${missingFields.join(', ')}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const dftFields = ['dftLow', 'dftHigh', 'dftAverage'];
    const outOfRangeFields = dftFields.filter(field => {
      const value = formData[field];
      if (!value || value === '') return false;
      const numValue = parseFloat(value);
      return !isNaN(numValue) && (numValue < 200 || numValue > 400);
    });

    if (outOfRangeFields.length > 0) {
      if (formData.finalStatus !== 'Rejected' && formData.finalStatus !== 'Hold for rework') {
        setSubmitMessage('DFT readings are outside the acceptable range (200-400 µm). Final status must be "Rejected" or "Hold for rework".');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    if (outOfRangeFields.length > 0 || formData.finalStatus === 'Rejected' || formData.finalStatus === 'Hold for rework') {
      if (!formData.releaseNotes || formData.releaseNotes.trim() === '') {
        setSubmitMessage('Release Notes are mandatory when Final Status is "Rejected" or "Hold for rework". Please provide an explanation.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    const submitFormData = { ...formData };

    if (submitFormData.finalStatus === 'Rejected' || submitFormData.finalStatus === 'Hold for rework') {
      setSubmitMessage(
        `⚠️ INSPECTION FAILED - Final status is "${submitFormData.finalStatus}". ` +
        `Please ensure all issues are documented and contact a senior inspector if needed.`
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    setIsSubmitting(true);
    if (!submitMessage.includes('FAILED')) {
      setSubmitMessage('');
    }

    try {
      await validateFormCompletion(formData.vesselSerial, 'paintQualityInspection');
    } catch (error) {
      console.error('Serial validation failed:', error);
      setSerialModalMessage(error.message || 'Vessel Serial Number not found in Vessel Inventory.');
      setSerialModalOpen(true);
      setIsSubmitting(false);
      return;
    }

    try {
      await sendPaintQualityEmail(submitFormData);
      localStorage.setItem('pvs_primerBatchNumber', formData.primerBatchNumber);
      localStorage.setItem('pvs_topcoatBatchNumber', formData.topcoatBatchNumber);
      await markFormCompleted(formData.vesselSerial, 'paintQualityInspection', formData.inspectionDate, { completedBy: formData.inspectorName });
      setShowSuccessModal(true);
      setTimeout(() => {
        setFormData({
          vesselSerial: '', capacityLitres: '', customerName: '', datePainted: getPreviousWorkingDay(),
          primerPaintUsed: 'HSP ZP LT Penguard', topcoatPaintUsed: '12B21 Jotun Hardtop Flexi',
          primerBatchNumber: formData.primerBatchNumber, topcoatBatchNumber: formData.topcoatBatchNumber, inspectionDate: new Date().toISOString().split('T')[0],
          inspectorName: 'Adrian Bates', primerApplicationMethod: 'Airless', primerThinnerUsed: 'Jotun Thinner No 17',
    topcoatApplicationMethod: 'Airless', topcoatThinnerUsed: 'Jotun Thinner No 10',
    primerOvenBakeCycleUsed: 'no', primerOvenTemperature: '', primerOvenDuration: '',
          topcoatOvenBakeCycleUsed: 'no', topcoatOvenTemperature: '', topcoatOvenDuration: '',
          requiredDFTMin: '200', requiredDFTMax: '400', dftLow: '', dftHigh: '', dftAverage: '', coverageUniform: 'no', runsOrSags: 'no',
          pinholes: 'no', orangePeel: 'no', drySpray: 'no', undersideFullyCovered: 'no',
          dataPlatesFreeOfOverspray: 'no', finalStatus: '', releaseNotes: '', additionalComments: ''
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting paint quality inspection form:', error);
      setSubmitMessage(error.message || 'Error submitting form. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateDFTStats = () => {
    const low = formData.dftLow ? parseFloat(formData.dftLow) : NaN;
    const high = formData.dftHigh ? parseFloat(formData.dftHigh) : NaN;
    const avg = formData.dftAverage ? parseFloat(formData.dftAverage) : NaN;
    const requiredMin = parseFloat(formData.requiredDFTMin);
    const requiredMax = parseFloat(formData.requiredDFTMax);
    let pass = 'N/A';
    if (!isNaN(low) && !isNaN(high) && !isNaN(requiredMin) && !isNaN(requiredMax)) {
      pass = low >= requiredMin && high <= requiredMax ? 'PASS' : 'FAIL';
    }
    return {
      min: !isNaN(low) ? low.toFixed(1) : 'N/A',
      max: !isNaN(high) ? high.toFixed(1) : 'N/A',
      avg: !isNaN(avg) ? avg.toFixed(1) : 'N/A',
      pass
    };
  };

  const dftStats = calculateDFTStats();

  const isDFTOutOfRange = (value) => {
    if (!value || value === '') return false;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return false;
    return numValue < 200 || numValue > 400;
  };

  const hasOutOfRangeDFT = () => {
    const dftFields = ['dftLow', 'dftHigh', 'dftAverage'];
    return dftFields.some(field => isDFTOutOfRange(formData[field]));
  };

  const anyDFTOutOfRange = hasOutOfRangeDFT();

  return (
    <PVSPageLayout className="py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="pvs-glass rounded-xl shadow-pvs-glow border border-pvs-accent/30">
          <div className="border-b border-pvs-border p-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-white">Paint Quality Inspection Form</h1>
                <p className="text-xs text-pvs-text-dim mt-1">Ref: PVS-PQI-001 | Version: 1.0</p>
              </div>
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
                title="Return to Forms"
              >
                <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
              </button>
            </div>
            <p className="text-pvs-text-dim text-center">LPG Vessel Paint QC Gate Documentation</p>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Submit Message at Top */}
            {submitMessage && (
              <div className={`p-5 rounded-lg border-2 shadow-md ${
                submitMessage.includes('INSPECTION FAILED')
                  ? 'bg-red-900/30 text-red-400 border-red-500'
                  : submitMessage.includes('Error') || submitMessage.includes('Please fill') || submitMessage.includes('not found') || submitMessage.includes('Unable to validate')
                  ? 'bg-red-900/30 text-red-400 border-red-500/30' 
                  : 'bg-green-900/30 text-pvs-accent border-green-500/30'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {submitMessage.includes('INSPECTION FAILED') ? (
                      <svg className="h-6 w-6 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : submitMessage.includes('Error') || submitMessage.includes('Please fill') || submitMessage.includes('not found') || submitMessage.includes('Unable to validate') ? (
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3 flex-1">
                    <p className={`${submitMessage.includes('INSPECTION FAILED') ? 'text-base font-bold' : 'text-sm font-medium'}`}>{submitMessage}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Asset Identification Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Asset Identification</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Vessel Serial *</label>
                  <input type="text" name="vesselSerial" value={formData.vesselSerial} onChange={handleInputChange} onBlur={handleSerialLookup} className="block w-full pvs-input" required />
                  {serialLookupState.loading && (
                    <p className="mt-1 text-xs text-pvs-text-dim">Looking up vessel…</p>
                  )}
                  {!serialLookupState.loading && serialLookupState.notFound && (
                    <p className="mt-1 text-xs text-red-400">Serial number not found in Vessel Inventory.</p>
                  )}
                  {!serialLookupState.loading && serialLookupState.scrapped && (
                    <p className="mt-1 text-xs text-red-400">{getScrappedFormMessage('paintQualityInspection')}</p>
                  )}
                </div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Vessel Water Capacity (Litres)</label><input type="number" name="capacityLitres" value={formData.capacityLitres} onChange={handleInputChange} placeholder={missingVesselFields.capacityLitres ? MISSING_PLACEHOLDER : ''} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Customer Name *</label><input type="text" name="customerName" value={formData.customerName} onChange={handleInputChange} placeholder={missingVesselFields.customerName ? MISSING_PLACEHOLDER : ''} className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Date Painted</label><input type="date" name="datePainted" value={formData.datePainted} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspector Name *</label><input type="text" name="inspectorName" value={formData.inspectorName} onChange={handleInputChange} className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Inspection Date *</label><input type="date" name="inspectionDate" value={formData.inspectionDate} onChange={handleInputChange} className="block w-full pvs-input" required /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Primer Paint Used</label><select name="primerPaintUsed" value={formData.primerPaintUsed} onChange={handleInputChange} className="block w-full pvs-select"><option value="">Select primer paint</option><option value="HSP ZP LT Penguard">HSP ZP LT Penguard</option><option value="Jotun Primer">Jotun Primer</option></select></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Primer Batch Number</label><input type="text" name="primerBatchNumber" value={formData.primerBatchNumber} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Topcoat Paint Used</label><select name="topcoatPaintUsed" value={formData.topcoatPaintUsed} onChange={handleInputChange} className="block w-full pvs-select"><option value="">Select topcoat paint</option><option value="12B21 Jotun Hardtop Flexi">12B21 Jotun Hardtop Flexi</option><option value="12B17 Jotun Hardtop Flexi">12B17 Jotun Hardtop Flexi</option><option value="White Jotun Hardtop Flexi">White Jotun Hardtop Flexi</option></select></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Topcoat Batch Number</label><input type="text" name="topcoatBatchNumber" value={formData.topcoatBatchNumber} onChange={handleInputChange} className="block w-full pvs-input" /></div>
              </div>
            </div>

            {/* Coating Application Details Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Coating Application Details</h2>
              <h3 className="text-lg font-semibold text-pvs-accent mb-3">Primer Coat</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Application Method</label><select name="primerApplicationMethod" value={formData.primerApplicationMethod} onChange={handleInputChange} className="block w-full pvs-select"><option value="">Select method</option><option value="Airless">Airless</option><option value="HVLP">HVLP</option><option value="Roller">Roller</option><option value="Brush">Brush</option><option value="Mixed">Mixed</option></select></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Primer Thinner Used</label><input type="text" name="primerThinnerUsed" value={formData.primerThinnerUsed} onChange={handleInputChange} placeholder="e.g., Jotun Thinner No. 17" className="block w-full pvs-input" /></div>
              </div>
              <h3 className="text-lg font-semibold text-pvs-accent mb-3">Topcoat</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Application Method</label><select name="topcoatApplicationMethod" value={formData.topcoatApplicationMethod} onChange={handleInputChange} className="block w-full pvs-select"><option value="">Select method</option><option value="Airless">Airless</option><option value="HVLP">HVLP</option><option value="Roller">Roller</option><option value="Brush">Brush</option><option value="Mixed">Mixed</option></select></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Topcoat Thinner Used</label><input type="text" name="topcoatThinnerUsed" value={formData.topcoatThinnerUsed} onChange={handleInputChange} placeholder="e.g., Jotun Thinner No. 17" className="block w-full pvs-input" /></div>
              </div>

              <h3 className="text-lg font-semibold text-pvs-accent mb-3">Oven/Bake Cycle (if applicable)</h3>
              
              {/* Primer Oven Bake */}
              <div className="mb-4 p-4 bg-pvs-card-light rounded-lg">
                <h4 className="text-md font-medium text-pvs-text-muted mb-3">Primer Oven Bake Cycle</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-pvs-text-muted">Used</label>
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium ${formData.primerOvenBakeCycleUsed === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, primerOvenBakeCycleUsed: formData.primerOvenBakeCycleUsed === 'yes' ? 'no' : 'yes', primerOvenTemperature: formData.primerOvenBakeCycleUsed === 'yes' ? '' : '60', primerOvenDuration: formData.primerOvenBakeCycleUsed === 'yes' ? '' : '45' }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:ring-offset-2 ${formData.primerOvenBakeCycleUsed === 'yes' ? 'bg-green-600' : 'bg-red-600'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.primerOvenBakeCycleUsed === 'yes' ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      <span className={`text-sm font-medium ${formData.primerOvenBakeCycleUsed === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                  {formData.primerOvenBakeCycleUsed === 'yes' && (
                    <>
                      <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Temperature (°C)</label><input type="number" name="primerOvenTemperature" value={formData.primerOvenTemperature} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                      <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Duration (minutes)</label><input type="number" name="primerOvenDuration" value={formData.primerOvenDuration} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                    </>
                  )}
                </div>
              </div>

              {/* Topcoat Oven Bake */}
              <div className="p-4 bg-pvs-card-light rounded-lg">
                <h4 className="text-md font-medium text-pvs-text-muted mb-3">Topcoat Oven Bake Cycle</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-pvs-text-muted">Used</label>
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium ${formData.topcoatOvenBakeCycleUsed === 'no' ? 'text-red-400' : 'text-pvs-text-dim'}`}>No</span>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, topcoatOvenBakeCycleUsed: formData.topcoatOvenBakeCycleUsed === 'yes' ? 'no' : 'yes', topcoatOvenTemperature: formData.topcoatOvenBakeCycleUsed === 'yes' ? '' : '45', topcoatOvenDuration: formData.topcoatOvenBakeCycleUsed === 'yes' ? '' : '45' }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:ring-offset-2 ${formData.topcoatOvenBakeCycleUsed === 'yes' ? 'bg-green-600' : 'bg-red-600'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.topcoatOvenBakeCycleUsed === 'yes' ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      <span className={`text-sm font-medium ${formData.topcoatOvenBakeCycleUsed === 'yes' ? 'text-green-400' : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                  {formData.topcoatOvenBakeCycleUsed === 'yes' && (
                    <>
                      <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Temperature (°C)</label><input type="number" name="topcoatOvenTemperature" value={formData.topcoatOvenTemperature} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                      <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Duration (minutes)</label><input type="number" name="topcoatOvenDuration" value={formData.topcoatOvenDuration} onChange={handleInputChange} className="block w-full pvs-input" /></div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* DFT Readings Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Dry Film Thickness (DFT) Readings</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Required DFT Min (µm) <span className="text-xs text-pvs-text-dim">(Fixed)</span></label><input type="number" name="requiredDFTMin" value={formData.requiredDFTMin} readOnly className="block w-full rounded-md border-pvs-border shadow-sm bg-pvs-card-light text-pvs-text-dim px-3 py-2 border cursor-not-allowed" /></div>
                <div><label className="block text-sm font-medium text-pvs-text-muted mb-2">Required DFT Max (µm) <span className="text-xs text-pvs-text-dim">(Fixed)</span></label><input type="number" name="requiredDFTMax" value={formData.requiredDFTMax} readOnly className="block w-full rounded-md border-pvs-border shadow-sm bg-pvs-card-light text-pvs-text-dim px-3 py-2 border cursor-not-allowed" /></div>
              </div>

              <h3 className="text-lg font-semibold text-pvs-accent mb-3">Actual DFT Readings (µm)</h3>
              <p className="text-sm text-pvs-text-dim mb-3">Values must be between 200-400 µm</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {[
                  { name: 'dftLow', label: 'Low' }, { name: 'dftHigh', label: 'High' }, { name: 'dftAverage', label: 'Average' }
                ].map(({ name, label }) => (
                  <div key={name}>
                    <label className={`block text-sm font-medium mb-2 ${isDFTOutOfRange(formData[name]) ? 'text-red-400' : 'text-pvs-text-muted'}`}>
                      {label} {isDFTOutOfRange(formData[name]) && <span className="text-xs">(Out of range!)</span>}
                    </label>
                    <input type="number" step="0.1" name={name} value={formData[name]} onChange={handleInputChange} className={`block w-full rounded-md shadow-sm px-3 py-2 border ${isDFTOutOfRange(formData[name]) ? 'border-red-500 bg-red-900/30 text-red-400 focus:border-red-500 focus:ring-red-500' : 'pvs-input'}`} />
                  </div>
                ))}
              </div>

              {/* DFT Statistics */}
              <div className="bg-pvs-card-light p-4 rounded-md border border-pvs-border">
                <h4 className="text-md font-semibold text-pvs-accent mb-2">DFT Statistics (Auto-calculated)</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div><p className="text-sm text-pvs-text-dim">Minimum DFT</p><p className="text-lg font-semibold text-white">{dftStats.min} µm</p></div>
                  <div><p className="text-sm text-pvs-text-dim">Maximum DFT</p><p className="text-lg font-semibold text-white">{dftStats.max} µm</p></div>
                  <div><p className="text-sm text-pvs-text-dim">Average DFT</p><p className="text-lg font-semibold text-white">{dftStats.avg} µm</p></div>
                  <div><p className="text-sm text-pvs-text-dim">Result</p><p className={`text-lg font-semibold ${dftStats.pass === 'PASS' ? 'text-green-400' : dftStats.pass === 'FAIL' ? 'text-red-400' : 'text-pvs-text-dim'}`}>{dftStats.pass}</p></div>
                </div>
              </div>
            </div>

            {/* Visual Inspection Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Visual Inspection Checklist</h2>
              <div className="space-y-3">
                {[
                  { field: 'coverageUniform', label: 'Coverage Uniform', goodValue: 'yes' },
                  { field: 'runsOrSags', label: 'Runs or Sags', goodValue: 'no' },
                  { field: 'pinholes', label: 'Pinholes', goodValue: 'no' },
                  { field: 'orangePeel', label: 'Orange Peel', goodValue: 'no' },
                  { field: 'drySpray', label: 'Dry Spray', goodValue: 'no' },
                  { field: 'undersideFullyCovered', label: 'Underside Fully Covered', goodValue: 'yes' },
                  { field: 'dataPlatesFreeOfOverspray', label: 'Data Plates and Valve Threads Free of Overspray', goodValue: 'yes' }
                ].map(({ field, label, goodValue }) => (
                  <div key={field} className="flex items-center justify-between">
                    <label className="text-sm font-medium text-pvs-text-muted">{label}</label>
                    <div className="flex items-center space-x-3">
                      <span className={`text-sm font-medium ${(goodValue === 'yes' ? formData[field] === 'no' : formData[field] === 'no') ? (goodValue === 'no' ? 'text-green-400' : 'text-red-400') : 'text-pvs-text-dim'}`}>No</span>
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, [field]: formData[field] === 'yes' ? 'no' : 'yes' }))} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${(goodValue === 'yes' ? formData[field] === 'yes' : formData[field] === 'no') ? 'bg-green-600' : 'bg-red-600'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData[field] === 'yes' ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                      <span className={`text-sm font-medium ${(goodValue === 'yes' ? formData[field] === 'yes' : formData[field] === 'yes') ? (goodValue === 'no' ? 'text-red-400' : 'text-green-400') : 'text-pvs-text-dim'}`}>Yes</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Release Decision Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Release Decision</h2>
              {anyDFTOutOfRange && (
                <div className="mb-4 p-4 bg-red-900/30 border-2 border-red-500 rounded-lg">
                  <div className="flex items-center">
                    <svg className="h-6 w-6 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-400 font-semibold">DFT readings are outside the acceptable range (200-400 µm). Final status must be "Rejected" or "Hold for rework" and Release Notes are mandatory.</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-pvs-text-muted mb-2">Final Status *</label>
                  <select name="finalStatus" value={formData.finalStatus} onChange={handleInputChange} className={`block w-full rounded-md shadow-sm px-3 py-2 border ${anyDFTOutOfRange ? 'border-red-500 bg-red-900/30 text-red-400' : 'pvs-select'}`} required>
                    {anyDFTOutOfRange ? (<><option value="">Select status</option><option value="Hold for rework">Hold for rework</option><option value="Rejected">Rejected</option></>) : (<><option value="">Select status</option><option value="Passed">Passed</option><option value="Passed with touch-ups">Passed with touch-ups</option><option value="Hold for rework">Hold for rework</option><option value="Rejected">Rejected</option></>)}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${anyDFTOutOfRange || formData.finalStatus === 'Rejected' || formData.finalStatus === 'Hold for rework' ? 'text-red-400' : 'text-pvs-text-muted'}`}>
                    Release Notes {(anyDFTOutOfRange || formData.finalStatus === 'Rejected' || formData.finalStatus === 'Hold for rework') && <span className="text-red-400">* (Required)</span>}
                  </label>
                  <textarea name="releaseNotes" value={formData.releaseNotes} onChange={handleInputChange} rows={3} required={anyDFTOutOfRange || formData.finalStatus === 'Rejected' || formData.finalStatus === 'Hold for rework'} className={`block w-full rounded-md shadow-sm px-3 py-2 border ${(anyDFTOutOfRange || formData.finalStatus === 'Rejected' || formData.finalStatus === 'Hold for rework') && !formData.releaseNotes ? 'border-red-500 bg-red-900/30 text-red-400' : 'pvs-input'}`} placeholder={anyDFTOutOfRange ? "REQUIRED: Explain DFT failure and any corrective actions needed..." : formData.finalStatus === 'Rejected' || formData.finalStatus === 'Hold for rework' ? "REQUIRED: Explain reason for rejection/rework..." : "Any notes regarding the release decision..."} />
                </div>
              </div>
            </div>

            {/* Additional Comments Section */}
            <div className="border-b border-pvs-border pb-6">
              <h2 className="text-xl font-semibold text-pvs-accent mb-4">Additional Comments</h2>
              <div>
                <label className="block text-sm font-medium text-pvs-text-muted mb-2">Additional Observations or Notes</label>
                <textarea name="additionalComments" value={formData.additionalComments} onChange={handleInputChange} rows={4} className="block w-full pvs-input" placeholder="Enter any additional comments, observations, or recommendations..." />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6">
              <button type="button" onClick={onBack} className="flex items-center justify-center gap-2 px-4 py-2 border border-pvs-accent/40 rounded-md shadow-sm text-sm font-medium text-pvs-accent bg-transparent hover:bg-pvs-accent/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50">
                <Home className="h-4 w-4" />
                Back to Forms
              </button>
              <button type="submit" disabled={isSubmitting} className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-semibold text-pvs-bg bg-pvs-accent hover:bg-pvs-accent-dim focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pvs-accent/50 disabled:opacity-50">
                {isSubmitting ? 'Submitting...' : 'Submit Paint Quality Inspection'}
              </button>
            </div>
          </form>
        </div>
      </div>
      <SerialValidationModal isOpen={serialModalOpen} message={serialModalMessage} onClose={closeSerialModal} />
      <FormSuccessModal isOpen={showSuccessModal} formName="Paint Quality Inspection" onClose={onBack} />
    </PVSPageLayout>
  );
};

export default PaintQualityInspectionForm;
